import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { api, cleanHandle } from "@/lib/api";
import { Confirm, Panel, People, PrimaryButton, Stat, Title, ago, uxd } from "@/components/admin/kit";

const SHIP = ["min", "pay", "dredge"];
// Settings shaped { name: number }: a group lists one field per entry, labelled with the name.
const MAPS = ["ad_prices", "look_prices", "flex_prices"];
const RENT_NAMES = ["Prime", "Central", "Edge"];
const GROUPS = [
  ["Start", [["start_uxd", "Starting UXD"], ["wealth_pivot", "Relative price pivot (worth)"]]],
  ["Trade runs & boost", [["visits_per_day", "Trade runs per day"], ["boost_step", "Boost per trader (%)"], ["boost_max", "Max boost (%)"],
    ["crate_prizes", "Crate prizes (comma list)"], ["crate_odds", "Crate odds per 1000 (comma list)"]]],
  ["Running costs, UXD per day", [["upkeep_berth", "Each extra berth"], ["upkeep_dredge", "Each dredge level above 1"], ["upkeep_warehouse", "Warehouse, per level"]]],
  ["Lucky cargo", [["lucky_mults", "Multipliers (comma list, top = jackpot)"], ["lucky_odds", "Odds per 1000 (comma list)"]]],
  ["Upgrades", [["berth_costs", "Berth prices (comma list)"], ["dredge_base", "Dredge price (× level²)"], ["dredge_max", "Max dredge level"],
    ["warehouse_cost", "Warehouse price (× 2 per level)"], ["warehouse_rate", "Warehouse UXD/h per level"], ["warehouse_hold_h", "Warehouse holds (hours)"],
    ["warehouse_max", "Max warehouse level"], ["floor_cost", "Floor base price (× 2 per floor)"]]],
  ["Ads", [["ad_max_hours", "Ad hours per person at a time"]]],
  ["Ad prices, UXD per hour", [["ad_prices"]]],
  ["Looks, UXD per change", [["look_prices"]]],
  ["Luxury items, UXD", [["flex_prices"]]],
  ["Fireworks", [["fireworks_price", "Price (UXD)"], ["fireworks_min", "Show lasts (minutes)"]]],
  ["Golden blimp & landmarks", [["blimp_price", "Blimp price (UXD)"], ["blimp_min", "Blimp lasts (minutes)"], ["blimp_boost_pct", "Blimp ship boost (%)"],
    ["landmark_price", "Landmark base price (UXD)"], ["landmark_hours", "Landmark lasts (hours)"]]],
  ["Sea Casino", [["casino_min", "Smallest bet"], ["casino_max", "Biggest bet"], ["casino_mults", "Multipliers (comma list, top = jackpot)"],
    ["casino_odds", "Odds per 1000 (comma list)"]]],
  ["Rail freight", [["rail_mult", "Goods sell for (%)"], ["rail_back_h", "Money comes back after (hours)"], ["rail_every_min", "Train leaves every (minutes)"],
    ["freight_min", "Smallest load (UXD)"]]],
  ["Harbour Lottery & rush", [["lottery_ticket", "Ticket price (UXD)"], ["lottery_win_pct", "Winner takes (% of tickets)"],
    ["rush_pct", "Rush a ship (% of its fare, full trip)"]]],
  ["Duels", [["duel_min", "Min stake"], ["duel_max", "Max stake (0 = none)"], ["duel_fee_pct", "Duel fee (%)"], ["duels_per_day", "Duels per day (0 = no limit)"],
    ["duel_hours", "Challenge lasts (hours)"], ["quick_free_win", "Free quick match prize"], ["quick_win_pct", "Quick win prize (% of stake)"],
    ["quick_owner_pct", "Quick match owner cut (%)"], ["quick_day_cap", "Quick winnings a day, grows with worth (0 = no cap)"]]],
  ["Mayor", [["mayor_fee", "Candidacy fee"], ["harbour_tax_pct", "Harbour tax (%)"], ["docks_day_pct", "Docks Day bonus (%)"],
    ["sink_mayor_pct", "Mayor's share of spent UXD (%)"]]],
];

// The form keeps every value as text: ships as "rowboat.min", the berth prices as "500, 1500", ad prices as "ad_prices.hero", rent as "rent_tiers.0.price".
// The rent names are shown, not edited, so they stay out of the form.
const flat = (s) => {
  const out = {};
  for (const [k, v] of Object.entries(s)) {
    if (k === "ships") Object.entries(v).forEach(([id, x]) => SHIP.forEach((f) => { out[`${id}.${f}`] = String(x[f]); }));
    else if (MAPS.includes(k)) Object.entries(v).forEach(([id, x]) => { out[`${k}.${id}`] = String(x); });
    else if (k === "rent_tiers") v.forEach(([ring, price], i) => { out[`${k}.${i}.ring`] = String(ring); out[`${k}.${i}.price`] = String(price); });
    else if (k !== "rent_names") out[k] = Array.isArray(v) ? v.join(", ") : String(v);
  }
  return out;
};
const same = (a, b) => a.replace(/\s/g, "") === b.replace(/\s/g, "");

// The form back into settings; what isn't a number goes to the server as is and comes back as a message.
const parse = (f, defaults) => {
  const out = {};
  for (const [k, v] of Object.entries(defaults)) {
    if (k === "ships") out[k] = Object.fromEntries(Object.entries(v).map(([id, x]) => [id, { ...x, ...Object.fromEntries(SHIP.map((s) => [s, Number(f[`${id}.${s}`])])) }]));
    else if (MAPS.includes(k)) out[k] = Object.fromEntries(Object.keys(v).map((id) => [id, Number(f[`${k}.${id}`])]));
    else if (k === "rent_tiers") out[k] = v.map((_, i) => [Number(f[`${k}.${i}.ring`]), Number(f[`${k}.${i}.price`])]);
    else if (k in f) out[k] = Array.isArray(v) ? f[k].split(",").map(Number) : Number(f[k]);
  }
  return out;
};

function Num({ k, f, set, d }) {
  const diff = !same(f[k], d[k]);
  return <Input value={f[k]} onChange={(e) => set(k, e.target.value)} title={diff ? `default ${d[k]}` : undefined}
    className={`h-8 ${diff ? "border-amber-400 bg-amber-50" : ""}`} data-testid={`eco-${k}`} />;
}

const Field = ({ k, label, ...rest }) => (
  <label className="block text-sm">
    <span className="font-semibold">{label}</span>
    <div className="mt-1"><Num k={k} {...rest} /></div>
    {!same(rest.f[k], rest.d[k]) && <span className="text-xs text-amber-700">default {rest.d[k]}</span>}
  </label>
);

const Section = ({ title, children }) => (
  <div className="rounded-xl border border-slate-200 p-4">
    <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-500">{title}</h3>
    {children}
  </div>
);

// Only the fields whose setting the server has; a group with none is left out.
function Group({ title, fields, ...common }) {
  const named = (k, x) => [x, x.slice(k.length + 1).replace(/^./, (c) => c.toUpperCase())];
  const shown = fields.flatMap(([k, label]) => (MAPS.includes(k)
    ? Object.keys(common.f).filter((x) => x.startsWith(`${k}.`)).map((x) => named(k, x))
    : [[k, label]])).filter(([k]) => k in common.f);
  if (!shown.length) return null;
  return (
    <Section title={title}>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">{shown.map(([k, label]) => <Field key={k} k={k} label={label} {...common} />)}</div>
    </Section>
  );
}

export function EconomyPanel() {
  const [d, setD] = useState(null);
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);
  const [g, setG] = useState({ h: "", amount: "", reason: "" });
  const take = (x) => { setD(x); setF(flat(x.settings)); };
  const load = useCallback((first) => api("/admin/economy").then((x) => { setD(x); if (first) setF(flat(x.settings)); }).catch((e) => toast.error(e.message)), []);
  useEffect(() => {
    load(true);
    const t = setInterval(() => load(false), 15000);
    return () => clearInterval(t);
  }, [load]);
  if (!d || !f) return <div className="text-slate-500">Loading…</div>;

  const set = (k, v) => setF({ ...f, [k]: v });
  const saved = flat(d.settings), dflt = flat(d.defaults);
  const dirty = Object.keys(f).some((k) => !same(f[k], saved[k]));
  const custom = Object.keys(f).filter((k) => !same(f[k], dflt[k])).length;
  const put = async (body, done) => {
    setBusy(true);
    try {
      take(await api("/admin/economy", { method: "PUT", body }));
      toast.success(done);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };
  // The server keeps exactly what it is sent, so send everything that differs from the defaults: the old tuning plus the edits.
  const save = () => put(Object.fromEntries(Object.entries(parse(f, d.defaults)).filter(([k, v]) => JSON.stringify(v) !== JSON.stringify(d.defaults[k]))), "Economy saved");

  const delta = Number(g.amount);
  const grant = async () => {
    try {
      const r = await api(`/admin/residents/${cleanHandle(g.h)}/uxd`, { method: "POST", body: { delta, reason: g.reason } });
      toast.success(`${delta > 0 ? "Granted" : "Fined"} ${uxd(Math.abs(delta))}. @${r.h} holds ${uxd(r.uxd)} now.`);
      setG({ h: "", amount: "", reason: "" });
      load(false);
    } catch (e) {
      toast.error(e.message);
    }
  };
  const row = (r) => `${r.worth.toLocaleString()} worth · ${r.week.toLocaleString()} wk · ${r.uxd.toLocaleString()} cash`;
  const m = d.money;
  const common = { f, set, d: dflt };

  return (
    <div className="space-y-5" data-testid="economy-panel">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
        <Stat label="UXD held" value={m.held.toLocaleString()} sub="cash in residents' hands" testId="eco-held" />
        <Stat label="Invested" value={m.invested.toLocaleString()} sub="total spent on upgrades" testId="eco-invested" />
        <Stat label="Created, 24h" value={m.created.toLocaleString()} sub="paid out to residents" testId="eco-created" />
        <Stat label="Removed, 24h" value={m.removed.toLocaleString()} sub="taken back by the city" testId="eco-removed" />
        <Stat label="Invested, 24h" value={m.invested_day.toLocaleString()} sub="spent on upgrades" testId="eco-invested-day" />
      </div>
      <div className="grid gap-5 md:grid-cols-2">
        <Panel><Title hint="Top 10 by what they earned since Monday.">Top earners this week</Title>
          {d.week.length ? <People rows={d.week} right={row} testId="eco-week" /> : <div className="text-sm text-slate-500">Nobody has earned anything yet.</div>}
        </Panel>
        <Panel><Title hint="Top 10 by worth: cash plus what they invested.">Richest</Title>
          <People rows={d.rich} right={row} testId="eco-rich" />
        </Panel>
      </div>
      <div className="grid gap-5 md:grid-cols-2">
        <Panel testId="eco-grant-panel">
          <Title hint="A positive amount grants UXD, a negative one fines (never below zero). Both show in the log.">Grant or fine</Title>
          <div className="space-y-3">
            <div className="grid gap-3 sm:grid-cols-2">
              <Input value={g.h} onChange={(e) => setG({ ...g, h: e.target.value })} placeholder="@handle" data-testid="eco-grant-handle" />
              <Input type="number" value={g.amount} onChange={(e) => setG({ ...g, amount: e.target.value })} placeholder="Amount (+ or -)" data-testid="eco-grant-amount" />
            </div>
            <Input value={g.reason} maxLength={140} onChange={(e) => setG({ ...g, reason: e.target.value })} placeholder="Reason" data-testid="eco-grant-reason" />
            <PrimaryButton onClick={grant} disabled={!cleanHandle(g.h) || !Number.isInteger(delta) || !delta} testId="eco-grant-button">
              {delta < 0 ? "Fine" : "Grant"}
            </PrimaryButton>
          </div>
        </Panel>
        <Panel testId="eco-log-panel">
          <Title hint="The latest grants and fines.">Admin log</Title>
          {d.log.length ? (
            <ul className="space-y-1.5 text-sm">
              {d.log.map((e, i) => (
                <li key={i} className="flex items-baseline gap-2">
                  <span className={`w-20 shrink-0 text-right font-semibold tabular-nums ${e.amount < 0 ? "text-rose-600" : "text-emerald-600"}`}>{e.amount > 0 ? "+" : ""}{e.amount.toLocaleString()}</span>
                  <span className="min-w-0 flex-1 truncate">@{e.h}{e.reason && <span className="text-slate-500"> · {e.reason}</span>}</span>
                  <span className="shrink-0 text-xs text-slate-400">{ago(e.at)}</span>
                </li>
              ))}
            </ul>
          ) : <div className="text-sm text-slate-500">Nothing yet.</div>}
        </Panel>
      </div>
      <Panel testId="eco-tuning-panel">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <Title hint={`${custom ? `${custom} changed from the defaults (highlighted)` : "Everything is at its default"}. Changes apply to the whole city within seconds.`}>Tuning</Title>
          <div className="flex gap-2">
            <Confirm title="Reset the whole economy?" action="Reset all" disabled={busy} testId="eco-reset"
              text="Every number goes back to its default, including any edits you haven't saved. Balances and docks stay as they are."
              onConfirm={() => put({}, "Back to the defaults")}>Reset all to defaults</Confirm>
            <PrimaryButton tone="brand" onClick={save} disabled={busy || !dirty} testId="eco-save">{busy ? "Saving…" : "Save changes"}</PrimaryButton>
          </div>
        </div>
        <div className="space-y-4">
          {GROUPS.slice(0, 2).map(([title, fields]) => <Group key={title} title={title} fields={fields} {...common} />)}
          <Section title="Ships">
            <div className="mb-3 max-w-[14rem]"><Field k="late_after_h" label="A ship is late after (hours)" {...common} /></div>
            <table className="w-full max-w-xl text-left text-sm" data-testid="eco-ships">
              <thead className="text-xs uppercase tracking-wide text-slate-500">
                <tr><th className="pb-1.5">Ship</th><th className="pb-1.5">Minutes</th><th className="pb-1.5">Pays</th><th className="pb-1.5">Dredge needed</th></tr>
              </thead>
              <tbody>
                {Object.entries(d.defaults.ships).map(([id, s]) => (
                  <tr key={id}>
                    <td className="py-1 pr-3 font-semibold">{s.name}</td>
                    {SHIP.map((x) => <td key={x} className="py-1 pr-3"><Num k={`${id}.${x}`} {...common} /></td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </Section>
          {"rent_tiers.0.ring" in f && (
            <Section title="Uptown rent">
              <table className="w-full max-w-md text-left text-sm" data-testid="eco-rent">
                <thead className="text-xs uppercase tracking-wide text-slate-500">
                  <tr><th className="pb-1.5">Tier</th><th className="pb-1.5">Rings out</th><th className="pb-1.5">Price per week</th></tr>
                </thead>
                <tbody>
                  {d.defaults.rent_tiers.map((_, i) => (
                    <tr key={i}>
                      <td className="py-1 pr-3 font-semibold">{(d.settings.rent_names || RENT_NAMES)[i]}</td>
                      {["ring", "price"].map((x) => <td key={x} className="py-1 pr-3"><Num k={`rent_tiers.${i}.${x}`} {...common} /></td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="mt-2 text-xs text-slate-500">Rings are blocks out from the civic core. A lot pays the first tier whose rings out reach it.</p>
            </Section>
          )}
          {GROUPS.slice(2).map(([title, fields]) => <Group key={title} title={title} fields={fields} {...common} />)}
        </div>
      </Panel>
    </div>
  );
}
